import { getIsPublishedAssignment } from "@/assignments/utils/assignmentUtils";
import { LANG, LANG_EN } from "@/constants/Language";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import contentTypes, { type ContentType } from "@/view/constants/contentTypes";
import { useGetTranslatorLanguages } from "@/view/useGetTranslatorLanguages/useGetTranslatorLanguages";
import { isArticle } from "@/view/utils/contentTypeChecks";
import getAssignmentSetting from "@/view/utils/getAssignmentSetting";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import useViewPageContentContext from "@/view/ViewPageContent/useViewPageContentContext";

import type {
  Assignment,
  EmptyAssignment,
} from "@/types/models/Assignment.interface";

// CMS will eventually provide this metadata
// for now, setting the restricted content list manually
const BLOCKED_PROVIDERS = new Set([
  "afptv-boclips",
  "agence-france-presse",
  "bloomberg",
  "bloomberg-boclips",
  "bloomber-tca",
  "columbia-university-press",
  "creative-commons",
  "dk-learning-makematic",
  "dk-publishing",
  "evening-standard",
  "cengage",
  "indiana-daily-student",
  "library-of-congress",
  "mississippi-history-now",
  "narratively",
  "national-archives",
  "national-cancer-institute",
  "national-endowment-for-the-humanities",
  "national-institutes-of-health",
  "noaa",
  "noaa-scijinks",
  "other",
  "oxford-university-press",
  "pew-research-center",
  "poetsorg",
  "project-syndicate",
  "public-school-review",
  "public-school-review-2",
  "quarto",
  "scientific-american",
  "state-california",
  "storyshares",
  "the-guardian",
  "the-guardian-boclips",
  "us-dept-health-human-services",
  "united-nations",
  "ushistory",
  "united-states-holocaust",
  "whha",
  "wonder-media-network",
]);

/**
 * Checks if the content provider supports translation
 * @param contentProviderSlug - The slug of the content provider
 * @returns boolean indicating if the provider supports translation
 */
const isContentProviderTranslationSupported = (
  contentProviderSlug?: string,
): boolean => {
  if (!contentProviderSlug) return false;
  return !BLOCKED_PROVIDERS.has(contentProviderSlug.toLowerCase());
};

/**
 * Checks if the content is in English
 * @param language - The language code of the content
 * @returns boolean indicating if the content is in English
 */
const isContentInEnglish = (language?: string): boolean => {
  return language === LANG_EN;
};

/**
 * Checks if the content is a video
 * @param contentType - The type of content
 * @returns boolean indicating if the content is a video
 */
const isContentVideo = (contentType?: ContentType): boolean => {
  return contentType === contentTypes.INTERACTIVE_VIDEO;
};

/**
 * Determines if translation should be hidden for students based on assignment settings
 * @param assignment - current assignment
 * @returns boolean indicating if translation should be hidden
 */
const isTranslationHiddenOnAssignment = (
  assignment?: Assignment | EmptyAssignment,
): boolean => {
  return getAssignmentSetting("hide_translation", assignment);
};

/**
 * Hook to determine translator visibility in different contexts
 *
 * This hook provides two visibility flags for different use cases:
 *
 * 1. isTranslatorVisible: Controls UI visibility of translation buttons and features
 *    - Used for showing/hiding translation UI elements in the content view
 *    - Controls both:
 *      * The translate button in the secondary action toolbar
 *      * The inline translation buttons next to each paragraph
 *    - Takes into account:
 *      * Student preview mode (isStudentPreviewOn)
 *      * User role (isStudent)
 *      * Assignment settings (translateActionHiddenForStudents)
 *      * Base visibility conditions (feature flag, content type, language, etc.)
 *
 * 2. isTranslatorAssignmentConfigVisible: Controls visibility of translation settings in assignment config
 *    - Used for showing/hiding translation settings in assignment configuration
 *    - Only depends on base visibility conditions:
 *      * User type (isLiteUser)
 *      * Content type (not video)
 *      * Content language (English)
 *      * Provider support (contentProviderSupportsTranslation)
 *    - Does not consider student-specific settings or preview mode
 *
 * 3. isTranslateActionUnavailable: Indicates if the translate action is unavailable due to content provider restrictions
 *   - Used to determine if the translate action should be displayed as unavailable
 *   - Returns true if the content provider is blocked from translation (but other conditions could be added in the future)
 *
 * @param selectedAssignment - The currently selected assignment
 * @param content - The content being viewed
 * @returns Object containing visibility flags for different contexts and which languages are visible/available for translations.
 */

const useTranslatorVisibility = ({
  selectedAssignment,
  language,
}: {
  selectedAssignment?: Assignment | EmptyAssignment;
  language?: LANG | undefined;
}) => {
  const { isLiteUser, isStudent } = useGetUserProperties();
  const { updatedAssignment } = useAssignmentReducerContext();
  const { content, contentMetadata } = useViewPageContentContext();

  const supportedLanguages = useGetTranslatorLanguages();
  const hasAvailableLanguages = Boolean(supportedLanguages.length);
  const provider = contentMetadata?.find(
    (meta) => meta.data.content_type === "PROVIDER",
  );
  const contentProviderSlug =
    provider?.data.slug ||
    (isArticle(content?.data)
      ? content?.data?.content_provider?.slug
      : undefined);

  const contentProviderSupportsTranslation =
    isContentProviderTranslationSupported(contentProviderSlug);

  const isVideo = isContentVideo(content?.content_type ?? "ARTICLE");
  const isEnglish = isContentInEnglish(language);

  // Base visibility conditions that apply to both UI and config
  const baseVisibilityConditions =
    !isVideo && isEnglish && hasAvailableLanguages;

  const currentAssignment = updatedAssignment ?? selectedAssignment;
  const translateActionHiddenOnAssignment =
    isTranslationHiddenOnAssignment(currentAssignment);
  // Students must be on a published assignment with translation enabled to see feature
  const hasPublishedAssignment = getIsPublishedAssignment(currentAssignment);
  const translateActionVisibleForStudents =
    hasPublishedAssignment &&
    !isLiteUser &&
    !translateActionHiddenOnAssignment &&
    contentProviderSupportsTranslation;

  // Teachers should see translation feature unless explicitly hidden by assignment settings
  // (either from live assignment config or selected/saved assignment)
  const translateActionVisibleForTeachers = !translateActionHiddenOnAssignment;

  // UI visibility for translation buttons and features
  const isTranslationActionVisible =
    baseVisibilityConditions &&
    (isStudent
      ? translateActionVisibleForStudents
      : translateActionVisibleForTeachers);

  // UI visibility for translation action where the translate action displays as unavailable
  const isTranslateActionUnavailable = !contentProviderSupportsTranslation;

  // Config visibility for assignment settings
  const isTranslatorAssignmentConfigVisible =
    baseVisibilityConditions && !isTranslateActionUnavailable && !isLiteUser;

  return {
    isTranslationActionVisible,
    isTranslateActionUnavailable,
    isTranslatorAssignmentConfigVisible,
    supportedLanguages,
  };
};

export default useTranslatorVisibility;
